import type { ReactElement } from "react";
import type { SocialLinksConfig } from "@gaole/shared";

type IconProps = { className?: string };

export function WhatsAppIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.42 9.42 0 0 1-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.99 6.67 2.77a9.37 9.37 0 0 1 2.76 6.68c0 5.2-4.24 9.43-9.45 9.43m8.03-17.46A11.27 11.27 0 0 0 12.05.72C5.79.72.7 5.8.7 12.06c0 2 .52 3.95 1.52 5.67L.6 23.6l6.01-1.58a11.33 11.33 0 0 0 5.43 1.38h.01c6.25 0 11.34-5.09 11.34-11.34 0-3.03-1.18-5.88-3.32-8.02" />
    </svg>
  );
}

export function InstagramIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
      <circle cx="12" cy="12" r="4.25" />
      <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function FacebookIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M13.5 21.9v-7.6h2.6l.4-3h-3V9.4c0-.87.24-1.46 1.49-1.46h1.6V5.25c-.28-.04-1.22-.12-2.32-.12-2.3 0-3.87 1.4-3.87 3.97v2.2H7.8v3h2.6v7.6C5.6 21.15 2 17 2 12 2 6.48 6.48 2 12 2s10 4.48 10 10c0 5-3.6 9.15-8.5 9.9" />
    </svg>
  );
}

export interface SocialLink {
  key: "whatsapp" | "instagram" | "facebook";
  label: string;
  href: string;
  Icon: (props: IconProps) => ReactElement;
  /** Brand fill for the floating button options. */
  bgClass: string;
}

/** Configured links only — empty settings are skipped so nothing renders as a dead link. */
export function socialLinks(social: SocialLinksConfig | undefined, fallbackWhatsappUrl?: string | null): SocialLink[] {
  const links: SocialLink[] = [];
  const whatsapp = social?.whatsappUrl ?? fallbackWhatsappUrl ?? null;
  if (whatsapp) {
    links.push({ key: "whatsapp", label: "WhatsApp", href: whatsapp, Icon: WhatsAppIcon, bgClass: "bg-[#25D366]" });
  }
  if (social?.instagramUrl) {
    links.push({
      key: "instagram",
      label: "Instagram",
      href: social.instagramUrl,
      Icon: InstagramIcon,
      bgClass: "bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af]",
    });
  }
  if (social?.facebookUrl) {
    links.push({ key: "facebook", label: "Facebook", href: social.facebookUrl, Icon: FacebookIcon, bgClass: "bg-[#1877F2]" });
  }
  return links;
}
